// InfoTooltip — tiny self-contained hint primitive. No popover library;
// hover OR keyboard focus on the trigger toggles the body. Body is
// absolutely positioned (default `top`) and stays close to the inline
// trigger so it works inside table cells / page headers without
// portaling.
import { useState, type ReactNode } from "react"
import { Info } from "lucide-react"
import { cn } from "@/lib/cn"

type Props = {
  children: ReactNode
  side?: "top" | "right" | "bottom"
  className?: string
}

const SIDE: Record<NonNullable<Props["side"]>, string> = {
  top:    "bottom-full left-1/2 -translate-x-1/2 mb-2",
  right:  "left-full top-1/2 -translate-y-1/2 ml-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
}

// Trigger is a <span>, not a <button>: the tooltip toggles on hover or
// keyboard focus only — no click action — so an interactive span with
// tabIndex avoids nesting button-in-button when this lives inside a
// tab/menu button (HTML disallows that nesting).
export function InfoTooltip({ children, side = "top", className }: Props) {
  const [open, setOpen] = useState(false)
  return (
    <span className="relative inline-flex">
      <span
        role="img"
        aria-label="More info"
        tabIndex={0}
        className={cn(
          "inline-flex cursor-help items-center justify-center p-0.5 text-app-muted outline-none hover:text-app-text focus-visible:text-app-text",
          className,
        )}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        // Only react to KEYBOARD focus (Tab nav), not programmatic auto-focus.
        // When a Dialog opens it grabs the first focusable child, which would
        // otherwise pop the tooltip uninvited.
        onFocus={(e) => { if (e.target.matches(":focus-visible")) setOpen(true) }}
        onBlur={() => setOpen(false)}
        onClick={(e) => e.stopPropagation()}
      >
        <Info size={14} strokeWidth={2} />
      </span>
      {open && (
        <span
          role="tooltip"
          className={cn(
            // normal-case + tracking-normal reset whatever typography the
            // trigger inherited (e.g. an uppercase tracking-wide label).
            // Without these, the body picks up the parent's transform and
            // becomes a wall of caps.
            "absolute z-20 block w-[280px] max-w-[280px] rounded-card border border-border bg-app-panel2 px-3 py-2 text-xs font-normal normal-case tracking-normal leading-relaxed text-app-text shadow-lg",
            SIDE[side],
          )}
        >
          {children}
        </span>
      )}
    </span>
  )
}
